<!--
  Page taskbar — the strip of open-page tabs that took the place of the
  "MadameFin Backoffice : <env>" title on the top navbar.
  Spec: docs/specs/2026-10-03-bo-taskbar-design.md §3, §5, §6.

  All decidable behaviour lives in src/utils/taskbar.js (this repo's jest
  cannot mount a .vue — reference-bo-fn-jest-infra); this file only renders,
  routes and persists.
-->
<template>
	<div class="page-taskbar" @click="closeMenu">
		<!-- Scroll buttons (spec 2026-10-06 §2.1 A9): only while the strip
		     overflows — its own scrollbar is hidden — and each one disabled at
		     its end. When to show / enable is taskbarScrollState's call. -->
		<button
			v-if="scroll.overflow"
			type="button"
			class="page-taskbar__btn page-taskbar__scroll page-taskbar__scroll--left"
			:disabled="!scroll.canLeft"
			:title="$t('taskbar.scroll_left')"
			:aria-label="$t('taskbar.scroll_left')"
			@click.stop="scrollStrip(-1)"
		>
			<i class="fa fa-chevron-left" aria-hidden="true"></i>
		</button>
		<div class="page-taskbar__strip" ref="strip" @scroll.passive="measureScroll">
			<!-- The tooltip carries the path as well as the label: the label is
			     not unique — two product-edit tabs are both "แก้ไขสินค้า" and the
			     id in the path is the only thing telling them apart. -->
			<div
				v-for="tab in tabs"
				:key="tab.key"
				class="page-taskbar__tab"
				:class="{ 'is-active': tab.key === activeKey, 'is-pinned': tab.pinned }"
				:title="tab.label + ' · ' + tab.key"
				@click="go(tab.key)"
				@mouseup.middle.prevent="close(tab.key)"
				@contextmenu.prevent="openMenu($event, tab.key)"
			>
				<i v-if="tab.pinned" class="fa fa-thumbtack page-taskbar__pinmark" aria-hidden="true"></i>
				<span class="page-taskbar__label">{{ tab.label }}</span>
				<!-- No pin control on an UNPINNED tab once five tabs are pinned:
				     the rule lives in taskbarCanPin so a spec can read it without
				     mounting this file. A pinned tab always keeps its control —
				     hiding that one would strand the user at five pins with no
				     way back. -->
				<button
					v-if="canPin(tab.key)"
					type="button"
					class="page-taskbar__btn page-taskbar__pin"
					:title="tab.pinned ? $t('taskbar.unpin') : $t('taskbar.pin')"
					:aria-label="tab.pinned ? $t('taskbar.unpin') : $t('taskbar.pin')"
					@click.stop="togglePin(tab.key)"
				>
					<i class="fa fa-thumbtack" aria-hidden="true"></i>
				</button>
				<button
					type="button"
					class="page-taskbar__btn page-taskbar__close"
					:title="$t('taskbar.close')"
					:aria-label="$t('taskbar.close')"
					@click.stop="close(tab.key)"
				>
					<i class="fa fa-times" aria-hidden="true"></i>
				</button>
			</div>
		</div>

		<button
			v-if="scroll.overflow"
			type="button"
			class="page-taskbar__btn page-taskbar__scroll page-taskbar__scroll--right"
			:disabled="!scroll.canRight"
			:title="$t('taskbar.scroll_right')"
			:aria-label="$t('taskbar.scroll_right')"
			@click.stop="scrollStrip(1)"
		>
			<i class="fa fa-chevron-right" aria-hidden="true"></i>
		</button>

		<button
			v-if="tabs.length"
			type="button"
			class="page-taskbar__btn page-taskbar__more"
			:title="$t('taskbar.close_all')"
			:aria-label="$t('taskbar.close_all')"
			@click.stop="openMenu($event, activeKey)"
		>
			<i class="fa fa-caret-down" aria-hidden="true"></i>
		</button>

		<ul v-if="menu.open" class="page-taskbar__menu" :style="{ left: menu.x + 'px' }">
			<li><a href="#" @click.prevent="closeOthers">{{ $t("taskbar.close_others") }}</a></li>
			<li><a href="#" @click.prevent="closeAll">{{ $t("taskbar.close_all") }}</a></li>
		</ul>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import {
	taskbarKey,
	taskbarCanPin,
	taskbarOpen,
	taskbarClose,
	taskbarTogglePin,
	taskbarSerialize,
	taskbarRestore,
	taskbarMerge,
	taskbarIsPage,
	taskbarLabel,
	taskbarPinsPayload,
	taskbarPinsSignature,
	taskbarAdoptServerPins,
	taskbarScrollState,
	taskbarScrollStep,
	taskbarRevealDelta
} from "@/utils/taskbar";
import { setLiveTabs } from "@/utils/taskbarLive";

// Where closing the last tab lands. Same page the sidebar's first entry points
// at, so the operator never ends up on a blank router view.
const HOME_PATH = "/backoffice/reports/dashboard";

// The storage suffix used when nobody is logged in. Nothing is ever WRITTEN
// under it (see persist / userKnown): a strip saved under "anon" would either
// be lost the moment the id arrives, or — worse — be read by whoever logs in
// next. It exists only so the key is still a string while we wait.
const ANON = "anon";

// Server sync of the PINNED tabs (docs/specs/2026-10-05-bo-taskbar-server-pins-design.md §4).
// Pin changes are coalesced for this long before one PUT goes out; a failed PUT
// is retried once after SYNC_RETRY_MS and then left until the next change.
const SYNC_DEBOUNCE_MS = 500;
const SYNC_RETRY_MS = 2000;

// The API client is resolved lazily (a `require` at first use) rather than
// imported at the top. A static `import ... from "@/resource"` drags http.js ->
// routes/router.js -> the whole route tree into this component's import graph,
// which in jest is a circular import ("Class extends value undefined") that
// kills the mount suite in tests/unit/taskbarPersistence.spec.js. In the app
// the module is already in the main bundle, so this costs nothing. A failure
// lands in pullPins' catch = "server unknown", i.e. pre-feature behaviour; it
// is remembered so a broken import is not retried on every pin change.
let taskbarApiCache;
function taskbarApi() {
	if (taskbarApiCache === undefined) {
		try {
			const m = require("@/resource");
			taskbarApiCache = (m && m.TaskbarService) || null;
		} catch (e) {
			taskbarApiCache = null;
		}
	}
	if (!taskbarApiCache) return Promise.reject(new Error("TaskbarService unavailable"));
	return Promise.resolve(taskbarApiCache);
}

// Data[0] of the {Status, Msg, Cause, Data:[item]} envelope, or null.
function firstItem(res) {
	const rows = res && res.data && res.data.Data;
	const row = Array.isArray(rows) && rows.length ? rows[0] : null;
	return row && typeof row === "object" ? row : null;
}

export default {
	name: "page-taskbar",
	data() {
		return {
			state: { tabs: [] },
			// WHICH user's storage has been read into `state`, or "" for none.
			// Not a boolean: the id changes under a live component (logout, a
			// colleague logging in) and a boolean said "loaded" about the
			// PREVIOUS user, which is how one person's strip got written under
			// another person's key. persist() refuses to write until this
			// matches the current userId.
			restoredFor: "",
			menu: { open: false, x: 0, key: "" },
			// Server sync of the pinned tabs:
			//   "unknown" — not asked yet, or the last GET failed: NEVER PUT
			//               (we do not know what another machine has saved, and
			//               pushing our pins blind could overwrite it);
			//   "server"  — the GET answered; pin changes are PUT;
			//   "local"   — the API has no table yet: localStorage only, exactly
			//               as before this feature, for the rest of the session.
			serverMode: "unknown",
			// taskbarPinsSignature of the pin set the server is known to hold
			// (or, while "unknown", the one we last saw, so only a real pin
			// change retries the GET).
			syncedSig: "",
			syncTimer: null,
			pullInFlight: false,
			// What the scroll buttons show (§2.1 A9) — taskbarScrollState's
			// answer for the strip as last measured.
			scroll: { overflow: false, canLeft: false, canRight: false }
		};
	},
	computed: {
		...mapGetters({ getUser: "Auth/getUser" }),
		tabs() {
			return this.state.tabs;
		},
		// By PATH (taskbarKey): /seller/orders?tab=completed highlights the
		// /seller/orders tab.
		activeKey() {
			return taskbarKey(this.$route);
		},
		// Per user: two people sharing a browser profile must not inherit each
		// other's pinned pages (some of which they may not even be allowed to
		// open).
		userId() {
			const u = this.getUser;
			// Auth/getUser is the object in src/store/auth_module.js: `id` is ""
			// until SET_USER runs. vuex-persistedstate rehydrates it before any
			// component is created, but a hard reload on a page whose session has
			// lapsed, or a login in progress, both land here with no id.
			return u && u.id ? String(u.id) : ANON;
		},
		// Whether we know WHOSE strip this is. Until we do we neither read nor
		// write: an "anon" bucket would be handed to the next person to log in.
		userKnown() {
			return this.userId !== ANON;
		},
		pinnedStorageKey() {
			return "mdf.taskbar.pinned." + this.userId;
		},
		// The loose tabs moved from sessionStorage to localStorage on
		// 2026-10-04: the owner logs out and back in during a working day and
		// expects the whole strip back, and sessionStorage cannot outlive a
		// session by definition. New key name rather than the old
		// "mdf.taskbar.session.<id>" so the two never read each other's shape.
		openStorageKey() {
			return "mdf.taskbar.open." + this.userId;
		}
	},
	watch: {
		// Mirror the strip into taskbarLive for DashboardLayout's per-tab
		// keep-alive (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md
		// §3.2). `immediate` is safe HERE, unlike the $route watcher below: it
		// only copies the array out, it never opens, persists or syncs. `state`
		// is always replaced, never mutated (src/utils/taskbar.js rule 1), so a
		// shallow watch sees every change.
		state: {
			immediate: true,
			handler() {
				setLiveTabs(this.state.tabs);
			}
		},
		// NOT `immediate: true`. Vue 2 builds watchers in initState(), which runs
		// BEFORE created() — an immediate handler here fired taskbarOpen() +
		// persist() before restore() had ever run, and persist() writes the
		// PINNED SUBSET of the in-memory state, which at that moment is []. Every
		// single page load used to overwrite the saved pins with an empty list and
		// then "restore" from the file it had just emptied (owner report, bo-stg:
		// "after F5 all tabs disappear, pinned included"). The first open now
		// happens in created(), after restore().
		$route() {
			this.openCurrent();
		},
		// A tab opened or closed changes the strip's scrollWidth; measure once
		// the DOM has it.
		tabs() {
			this.$nextTick(this.measureScroll);
		},
		// Switching tab (sidebar, link, back button) can land on a tab that is
		// scrolled out of sight — bring it in. nextTick: a newly opened tab is
		// only in the DOM after this flush.
		activeKey() {
			this.$nextTick(this.revealActive);
		},
		// The id arrived (login finished / store rehydrated late), or a different
		// colleague logged in in this tab. Either way: adopt THEIR strip. The
		// current page is re-opened on top of it so nobody loses the page they
		// are looking at.
		userId(now) {
			// Whoever was there before, a save pending for them must not go out
			// under the next session's token. localStorage is NOT cleared (#325).
			clearTimeout(this.syncTimer);
			this.syncTimer = null;
			this.serverMode = "unknown";
			this.syncedSig = "";
			if (!this.userKnown) {
				// Logged out. Drop the strip from memory so the next person to
				// log in cannot see it for even one tick, and write NOTHING:
				// `restoredFor` no longer matches, so persist() is closed.
				this.state = { tabs: [] };
				this.restoredFor = "";
				return;
			}
			if (this.restoredFor === now) {
				this.pullPins();
				return;
			}
			this.restore();
			this.openCurrent();
			this.pullPins();
		}
	},
	created() {
		this.restore();
		this.openCurrent();
		// localStorage first, the server second: the strip is on screen before
		// the GET returns, and the server's pins replace ours when it does.
		this.pullPins();
	},
	mounted() {
		document.addEventListener("click", this.closeMenu);
		// Re-measure on anything that can change the strip's width without
		// touching `tabs`: the window, the sidebar collapsing, the language
		// switch relabelling every tab. ResizeObserver is the precise one;
		// `resize` is the fallback for a browser without it.
		window.addEventListener("resize", this.measureScroll);
		if (typeof window.ResizeObserver === "function" && this.$refs.strip) {
			try {
				this.resizeObserver = new window.ResizeObserver(() => this.measureScroll());
				this.resizeObserver.observe(this.$refs.strip);
			} catch (e) {
				this.resizeObserver = null;
			}
		}
		this.$nextTick(() => {
			this.measureScroll();
			this.revealActive();
		});
	},
	beforeDestroy() {
		// The layout (and its keep-alive) goes with us; nobody should read a
		// dead strip's tabs as live ones.
		setLiveTabs([]);
		document.removeEventListener("click", this.closeMenu);
		window.removeEventListener("resize", this.measureScroll);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
		clearTimeout(this.syncTimer);
		this.syncTimer = null;
	},
	methods: {
		// Read the strip and ask taskbarScrollState what the buttons should
		// show. Called on every scroll event, so only a real change is
		// assigned (no re-render per pixel scrolled).
		measureScroll() {
			const el = this.$refs.strip;
			if (!el) return;
			const next = taskbarScrollState({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth, scrollWidth: el.scrollWidth });
			const cur = this.scroll;
			if (next.overflow !== cur.overflow || next.canLeft !== cur.canLeft || next.canRight !== cur.canRight) {
				this.scroll = next;
			}
		},
		// dir: -1 left, +1 right. Smooth where the browser can; the scroll
		// event that follows re-measures.
		scrollStrip(dir) {
			const el = this.$refs.strip;
			if (!el) return;
			const delta = dir * taskbarScrollStep(el.clientWidth);
			try {
				if (typeof el.scrollBy === "function") el.scrollBy({ left: delta, behavior: "smooth" });
				else el.scrollLeft += delta;
			} catch (e) {
				el.scrollLeft += delta;
			}
		},
		// Bring the active tab fully into the strip's view. Moves ONLY the
		// strip (see taskbarRevealDelta for why not the DOM scroll-into-view API). Best-effort:
		// a layout read failing must never break navigation.
		revealActive() {
			try {
				const el = this.$refs.strip;
				if (!el) return;
				const tab = el.querySelector(".page-taskbar__tab.is-active");
				if (!tab) return;
				const delta = taskbarRevealDelta(el.getBoundingClientRect(), tab.getBoundingClientRect());
				if (delta) el.scrollLeft += delta;
				this.measureScroll();
			} catch (e) {
				/* layout unavailable — leave the strip where it is */
			}
		},
		// vue-i18n's $t, detached from `this` so the util stays vue-free.
		translate(key) {
			return this.$t(key);
		},
		// Put the page we are on into the strip and save. The single entry point
		// for "a route became current" — created() and the $route watcher both
		// go through it, so the open/persist pair cannot drift apart again.
		openCurrent() {
			// The login screen is not a backoffice page and must never become a
			// tab — see taskbarIsPage.
			if (!taskbarIsPage(this.$route)) return;
			this.state = taskbarOpen(this.state, this.$route, this.translate);
			this.persist();
		},
		// `key` is a tab's identity (its path). The tab's `href` — the full path
		// of the last visit, query included — is where we actually go, so the
		// orders tab reopens on ?tab=completed if that is where it was left. A
		// key with no tab (HOME_PATH) is navigated to as-is.
		go(key) {
			if (!key || key === this.activeKey) return;
			const tab = this.state.tabs.find(t => t.key === key);
			const target = (tab && tab.href) || key;
			// NavigationDuplicated rejects loudly on vue-router >= 3.1; the user
			// clicking the tab they are already on is not an error worth a
			// console full of red.
			const p = this.$router.push(target);
			if (p && typeof p.catch === "function") p.catch(() => {});
		},
		// Where to send the user after a close. `go()` is a no-op when the
		// target is the page we are already on — and then no navigation fires,
		// so the $route watcher never runs and the tab we just removed is not
		// put back. Closing the only tab while standing on the dashboard left
		// the strip empty until the next click. Re-open the current route
		// instead of navigating to it.
		goOrReopen(target) {
			const next = target || HOME_PATH;
			if (next === this.activeKey) {
				this.state = taskbarOpen(this.state, this.$route, this.translate);
				this.persist();
				return;
			}
			this.go(next);
		},
		// allowEmpty: the three places below are the user emptying the pinned
		// set on purpose. Everywhere else an empty set means "not loaded yet".
		close(key) {
			const wasActive = key === this.activeKey;
			const { state, nextKey } = taskbarClose(this.state, key);
			this.state = state;
			this.persist({ allowEmpty: true });
			if (wasActive) this.goOrReopen(nextKey);
		},
		// Template-facing wrapper; the rule itself is in src/utils/taskbar.js.
		canPin(key) {
			return taskbarCanPin(this.state.tabs, key);
		},
		togglePin(key) {
			this.state = taskbarTogglePin(this.state, key);
			this.persist({ allowEmpty: true });
		},
		openMenu(event, key) {
			const host = this.$el.getBoundingClientRect();
			this.menu = { open: true, x: Math.max(0, event.clientX - host.left), key: key };
		},
		closeMenu() {
			if (this.menu.open) this.menu = { open: false, x: 0, key: "" };
		},
		// "ปิดแท็บอื่น" / "ปิดทั้งหมด" never touch pinned tabs — pinning is the
		// user saying "keep this", and a menu item two pixels away from it must
		// not quietly override that.
		closeOthers() {
			const keep = this.menu.key || this.activeKey;
			this.closeMany(tab => tab.pinned || tab.key === keep);
		},
		closeAll() {
			this.closeMany(tab => tab.pinned);
		},
		closeMany(keepFn) {
			let state = this.state;
			for (const tab of this.state.tabs) {
				if (keepFn(tab)) continue;
				state = taskbarClose(state, tab.key).state;
			}
			this.state = state;
			this.closeMenu();
			this.persist({ allowEmpty: true });
			// The page we are standing on may have just been closed.
			if (!this.state.tabs.some(tab => tab.key === this.activeKey)) {
				const next = this.state.tabs.length ? this.state.tabs[this.state.tabs.length - 1].key : HOME_PATH;
				this.goOrReopen(next);
			}
		},
		// Storage is best-effort on purpose: Safari private mode throws on
		// write and a full quota throws on set. A taskbar that cannot remember
		// itself is a small loss; a navbar that throws on every navigation is
		// the whole backoffice.
		restore() {
			// No id yet: do NOT read (there is nothing under "anon" worth having)
			// and leave `restored` false so persist() stays quiet. The userId
			// watcher calls us again the moment we know who this is.
			if (!this.userKnown) return;
			let pinned = { tabs: [] };
			let open = { tabs: [] };
			try {
				pinned = taskbarRestore(window.localStorage.getItem(this.pinnedStorageKey));
			} catch (e) {
				pinned = { tabs: [] };
			}
			try {
				open = taskbarRestore(window.localStorage.getItem(this.openStorageKey));
			} catch (e) {
				open = { tabs: [] };
			}
			// REPLACES the in-memory strip rather than merging into it. On a
			// colleague logging in over the top of this session, merging would
			// show them the previous person's pages; openCurrent() puts the page
			// actually on screen back, which is the only one worth carrying over.
			this.state = this.relabelBare(taskbarMerge(pinned, open));
			this.restoredFor = this.userId;
		},
		// A restored tab whose label is just its key (no saved label, or one
		// dropped because the page was renamed — src/utils/legacyPaths.js) gets
		// the page's name in this machine's language. Others are left alone.
		relabelBare(state) {
			const tabs = state && Array.isArray(state.tabs) ? state.tabs : [];
			if (!tabs.some(tab => tab.label === tab.key)) return state;
			return {
				...state,
				tabs: tabs.map(tab => {
					if (tab.label !== tab.key) return tab;
					const fresh = this.labelForKey(tab.key);
					return fresh ? { ...tab, label: fresh } : tab;
				})
			};
		},
		// Does storage currently hold at least one pinned tab? Used to refuse a
		// write that would empty a non-empty saved list by accident.
		hasStoredPinned() {
			try {
				return taskbarRestore(window.localStorage.getItem(this.pinnedStorageKey)).tabs.some(tab => tab.pinned);
			} catch (e) {
				return false;
			}
		},
		// `allowEmpty` = the user themselves just closed or unpinned something,
		// so an empty pinned list is the truth and must be written. Without the
		// flag an empty list is treated as "we do not know yet" and the saved one
		// is left alone — the difference between a strip the user emptied and a
		// strip we failed to load.
		persist(options) {
			// Two locks, both load-bearing:
			//   - userKnown: nothing is EVER written under the "anon" key. A
			//     strip parked there is either lost when the id arrives or
			//     handed to whoever logs in next.
			//   - restoredFor: we have read THIS user's storage, so the state
			//     about to be written is theirs and is complete.
			if (!this.userKnown) return;
			if (this.restoredFor !== this.userId) return;
			const allowEmpty = !!(options && options.allowEmpty);
			try {
				const pinnedOnly = { tabs: this.state.tabs.filter(tab => tab.pinned) };
				if (pinnedOnly.tabs.length || allowEmpty || !this.hasStoredPinned()) {
					window.localStorage.setItem(this.pinnedStorageKey, taskbarSerialize(pinnedOnly));
				}
			} catch (e) {
				/* storage unavailable — the strip still works for this session */
			}
			try {
				window.localStorage.setItem(this.openStorageKey, taskbarSerialize(this.state));
			} catch (e) {
				/* idem */
			}
			// The one place every pin change passes through (togglePin, close,
			// closeMany, the clamp on restore), so the server is told about all
			// of them from here.
			this.syncPinsIfChanged();
		},
		// Has the pin set moved away from what the server holds? Then schedule a
		// PUT ("server"), or — if we never managed to read the server — try the
		// GET again ("unknown"). Never anything in "local".
		syncPinsIfChanged() {
			if (!this.userKnown) return;
			const sig = taskbarPinsSignature(this.state);
			if (sig === this.syncedSig) return;
			if (this.serverMode === "server") {
				this.scheduleSync();
			} else if (this.serverMode === "unknown") {
				this.syncedSig = sig;
				this.pullPins();
			}
		},
		scheduleSync() {
			clearTimeout(this.syncTimer);
			this.syncTimer = setTimeout(() => {
				this.syncTimer = null;
				this.pushPins(true);
			}, SYNC_DEBOUNCE_MS);
		},
		// GET the server's pins. Results that come back for a user who is no
		// longer the one logged in (or a destroyed component) are dropped.
		async pullPins() {
			if (!this.userKnown || this.pullInFlight) return;
			const uid = this.userId;
			this.pullInFlight = true;
			let item = null;
			let failed = false;
			try {
				const api = await taskbarApi();
				item = firstItem(await api.Get());
				if (!item) failed = true;
			} catch (e) {
				failed = true;
			}
			this.pullInFlight = false;
			if (this._isDestroyed || this.userId !== uid || this.restoredFor !== uid) return;
			if (failed) {
				// Offline / 500: stay "unknown" and push nothing. The next pin
				// change asks again (syncPinsIfChanged).
				this.serverMode = "unknown";
				this.syncedSig = taskbarPinsSignature(this.state);
				return;
			}
			if (item.storage === "local") {
				// No table on this API yet: behave exactly as before the feature.
				this.serverMode = "local";
				return;
			}
			if (item.updated_at) {
				// The server has a row: it wins. Local-only pins become ordinary
				// tabs (taskbarAdoptServerPins), the page on screen stays open.
				let state = taskbarAdoptServerPins(this.state, item.pinned, this.labelForKey);
				if (taskbarIsPage(this.$route)) state = taskbarOpen(state, this.$route, this.translate);
				this.state = state;
				this.syncedSig = taskbarPinsSignature(this.state);
				this.serverMode = "server";
				// allowEmpty: an empty server set is the truth ("unpinned them
				// all on the other machine"), not "not loaded yet".
				this.persist({ allowEmpty: true });
				return;
			}
			// No row yet: this user's first time. Our local pins (if any) become
			// the starting set — pushed once, right now, not debounced.
			this.serverMode = "server";
			this.syncedSig = "";
			if (taskbarPinsPayload(this.state).length) this.pushPins(true);
		},
		// PUT the pinned set. One retry after SYNC_RETRY_MS, then silence: the
		// pins are still in localStorage and the next change tries again. No
		// toast — a failed save here must never interrupt the page.
		async pushPins(retry) {
			if (!this.userKnown || this.serverMode !== "server") return;
			const uid = this.userId;
			const pinned = taskbarPinsPayload(this.state);
			const sig = pinned.map(pin => pin.key).join("\n");
			let item = null;
			let failed = false;
			try {
				const api = await taskbarApi();
				item = firstItem(await api.Update({ pinned }));
			} catch (e) {
				failed = true;
			}
			if (this._isDestroyed || this.userId !== uid || this.serverMode !== "server") return;
			if (failed) {
				if (retry) {
					clearTimeout(this.syncTimer);
					this.syncTimer = setTimeout(() => {
						this.syncTimer = null;
						this.pushPins(false);
					}, SYNC_RETRY_MS);
				}
				return;
			}
			if (item && item.storage === "local") {
				this.serverMode = "local";
				return;
			}
			this.syncedSig = sig;
			// Changed again while the PUT was in flight? Send that too.
			if (taskbarPinsSignature(this.state) !== sig && !this.syncTimer) this.scheduleSync();
		},
		// Label of a pinned key in THIS machine's language, if the key still
		// resolves to a route here; otherwise the server's label is kept.
		labelForKey(key) {
			try {
				const resolved = this.$router.resolve(key);
				const route = resolved && resolved.route;
				if (!route || !route.matched || !route.matched.length) return "";
				return taskbarLabel(route, this.translate);
			} catch (e) {
				return "";
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.page-taskbar {
	position: relative;
	display: flex;
	/* Full height of the bar (§2.1 A7): stretch inside .navbar-brand-row
	   (whose .align-items-center would otherwise centre us) and let the strip
	   and the buttons stretch in turn. */
	align-items: stretch;
	align-self: stretch;
	min-width: 0;
	flex: 1 1 auto;
	/* The navbar is .bg-info/.navbar-dark ($info = #0065c3), so the strip is
	   white-on-blue: 5.75:1. Inactive tabs are tinted with a BLACK veil rather
	   than a white one — a white veil at .18 composites to #2e81ce and drops
	   the label to 4.10:1, under AA. Darkening only raises the ratio. The
	   active tab inverts to the theme ink on solid white.
	   Allowlisted (measured, not asserted) in tests/unit/contrast/hardcoded.js. */
	color: #fff;
}

/* Round 2 (2026-10-06, docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md
   §2.1, owner: "แถบด้านบนกว้างเกินไป ทำให้พอดีตัวอักษร ที่ให้ขยายเป็นความสูง ชิดด้านบน
   ล่าง และซ้าย กรณีแถบล้นหน้าให้ทำปุ่มเลื่อนซ้ายขวาให้ด้วย"):
     A6 each tab is as wide as its text (flex: 0 0 auto, no floor), capped at
        22rem with an ellipsis — round 1's equal-share stretch is gone;
     A7 tabs fill the bar's HEIGHT, flush top and bottom (align-self: stretch
        all the way down from the navbar, which is 36px with no padding —
        DashboardNavbar.vue);
     A9 the strip's scrollbar is hidden; the ‹ › buttons scroll it instead.
        Wheel / trackpad still scroll it (overflow-x stays auto).
   The strip itself still takes all the room left (A1). Layout only — the one
   colour-ish addition is the disabled scroll button's opacity, below.
   tests/unit/taskbarFill.spec.js resolves the cascade for all of this. */
.page-taskbar__strip {
	display: flex;
	flex-wrap: nowrap;
	align-items: stretch;
	align-self: stretch;
	flex: 1 1 auto;
	width: 100%;
	overflow-x: auto;
	overflow-y: hidden;
	min-width: 0;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
}

.page-taskbar__tab {
	display: inline-flex;
	align-items: center;
	align-self: stretch;
	flex: 0 0 auto;
	max-width: 22rem;
	margin-right: 2px;
	margin-bottom: 0;
	padding: 0 0.4rem 0 0.6rem;
	border-radius: 0.25rem 0.25rem 0 0;
	background: rgba(0, 0, 0, 0.18);
	font-size: 0.8125rem;
	line-height: 1.2;
	cursor: pointer;
	user-select: none;
	white-space: nowrap;

	&:hover {
		background: rgba(0, 0, 0, 0.32);
	}

	&.is-active {
		background: #fff;
		color: #32325d;
		font-weight: 600;
	}
}

/* Takes whatever the tab has spare; min-width 0 lets it shrink below its text
   so the ellipsis eats the label, never the pin / close buttons. */
.page-taskbar__label {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.page-taskbar__pinmark {
	margin-right: 0.3rem;
	font-size: 0.65rem;
	opacity: 0.85;
}

.page-taskbar__btn {
	flex: 0 0 auto;
	margin-left: 0.3rem;
	padding: 0 0.2rem;
	border: 0;
	background: transparent;
	color: inherit;
	font-size: 0.7rem;
	line-height: 1;
	opacity: 0.75;
	cursor: pointer;

	&:hover,
	&:focus {
		opacity: 1;
		outline: none;
	}
}

.page-taskbar__pin {
	opacity: 0.5;
}

.is-pinned .page-taskbar__pin {
	opacity: 1;
}

/* ‹ › (§2.1 A9). Same ink as every other taskbar control (.page-taskbar__btn
   inherits #fff); full bar height so the hit target is the whole strip edge. */
.page-taskbar__scroll {
	flex: 0 0 auto;
	align-self: stretch;
	margin-left: 0;
	padding: 0 0.45rem;
	font-size: 0.7rem;

	&:disabled {
		/* Disabled controls are exempt from WCAG 1.4.3; the contrast suite
		   tracks color/background only. Dimmed so "nothing more this way" is
		   visible at a glance. */
		opacity: 0.35;
		cursor: default;
	}
}

.page-taskbar__more {
	flex: 0 0 auto;
	align-self: center;
	margin-left: 0.25rem;
	font-size: 0.9rem;
	opacity: 1;
}

.page-taskbar__menu {
	position: absolute;
	top: 100%;
	z-index: 1030;
	margin: 0;
	padding: 0.25rem 0;
	list-style: none;
	min-width: 10rem;
	background: #fff;
	border-radius: 0.25rem;
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

	a {
		display: block;
		padding: 0.35rem 0.9rem;
		color: #32325d;
		font-size: 0.8125rem;
		white-space: nowrap;

		&:hover {
			background: #f6f9fc;
			text-decoration: none;
		}
	}
}
</style>
